<script setup lang="ts">
import { computed } from 'vue'

import raldoMark from '../../../assets/brand/raldo-mark.svg?raw'

type MarkSize = 'icon' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const props = withDefaults(
  defineProps<{
    /** Mark size: `icon` (20px, the surrounding icon size token) · `xs` (24px) · `sm` (32px) · `md` (40px, default) · `lg` (48px) · `xl` (64px). */
    size?: MarkSize
    wordmark?: boolean
    /** Hides the mark from assistive tech when a visible label already names it. */
    decorative?: boolean
    /** Paints the glyph in the surrounding ink, such as a badge's, instead of primary ink. */
    inherit?: boolean
  }>(),
  {
    size: 'md',
    wordmark: false,
    decorative: false,
    inherit: false,
  },
)

const SIZE_CLASSES: Record<MarkSize, string> = {
  icon: 'size-(--icon-size)',
  xs: 'size-12',
  sm: 'size-16',
  md: 'size-20',
  lg: 'size-24',
  xl: 'size-32',
}

const WORDMARK_CLASSES: Record<MarkSize, string> = {
  icon: 'text-body-sm-emphasized',
  xs: 'text-body-sm-emphasized',
  sm: 'text-h3',
  md: 'text-h2',
  lg: 'text-h1',
  xl: 'text-h1',
}

const WORDMARK_GAP_CLASSES: Record<MarkSize, string> = {
  icon: 'gap-2',
  xs: 'gap-2',
  sm: 'gap-4',
  md: 'gap-4',
  lg: 'gap-4',
  xl: 'gap-4',
}

const sizeClass = computed(() => SIZE_CLASSES[props.size])
const inkClass = computed(() => (props.inherit ? '' : 'text-text-primary'))
const wordmarkClass = computed(() => WORDMARK_CLASSES[props.size])
const wordmarkGapClass = computed(() => WORDMARK_GAP_CLASSES[props.size])
</script>

<template>
  <span v-if="wordmark" :class="['inline-flex select-none items-center', wordmarkGapClass]">
    <span
      data-mark="glyph"
      :class="['inline-block shrink-0 [&>svg]:size-full', inkClass, sizeClass]"
      :role="decorative ? undefined : 'img'"
      :aria-label="decorative ? undefined : 'Raldo'"
      :aria-hidden="decorative || undefined"
      v-html="raldoMark"
    />
    <span data-mark="word" :class="[wordmarkClass, 'text-text-primary']">raldo</span>
  </span>

  <span
    v-else
    data-mark="glyph"
    :class="['inline-block shrink-0 select-none [&>svg]:size-full', inkClass, sizeClass]"
    :role="decorative ? undefined : 'img'"
    :aria-label="decorative ? undefined : 'Raldo'"
    :aria-hidden="decorative || undefined"
    v-html="raldoMark"
  />
</template>
